import React, { useEffect, useContext, useState } from 'react'
import PropTypes from 'prop-types'
import httpStatus from 'http-status'
import { RevalidationCheckPinCodeTemplate } from '../../templates/RevalidationCheckPinCodeTemplate'
import { CountdownContext } from '../../../util/providers/CountdownProvider'
import { revalidationCheckAttempts } from '../../../api/attemptCheckAPI'
import { RevalidationContext } from '../../../util/providers/RevalidationProvider'
import {
  confirmationCheckPinCode,
  emailChangeCheckPinCode,
  sendPinCode,
} from '../../../api/pinCodeApi'
import { isValidEmail } from '../../../util/validators'

const defaultRemainingAttempts = 4

export const RevalidationEmailCheckPinCodePage = ({ onBackClick, onSuccess }) => {
  const { lockTime, checkAttemptLockTime } = useContext(CountdownContext)
  const { email, token, sendEmailRequestId, lurker } = useContext(RevalidationContext)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showResendCodeSuccessMessage, setShowResendCodeSuccessMessage] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [isResending, setIsResending] = useState(false)

  const checkAttempts = () => revalidationCheckAttempts(token)

  const onCheckAttempts = () => checkAttemptLockTime(checkAttempts)

  useEffect(() => {
    lurker.pageLoadEvt('revalidate_email_check')

    onCheckAttempts()
  }, [])

  const resendEmailPicode = () => {
    const newEmail = isValidEmail(email?.value) ? email?.value : null

    lurker.clickEvt('resend_pincode')

    setIsResending(true)

    sendPinCode(newEmail, 'EMAIL', token)
      .then(() => {
        lurker.viewMessageEvt('pincode_email_sent', { resend: true })
        setShowResendCodeSuccessMessage(true)
      })
      .catch(error => {
        lurker.viewMessageEvt('pincode_email_send_error', { resend: true })
        onApiError(error)
      })
      .finally(() => {
        setIsResending(false)

        onCheckAttempts()
      })
  }

  const closeResendCodeSuccessMessage = () => {
    setShowResendCodeSuccessMessage(false)
  }

  const onApiError = (error = {}) => {
    const { response = {} } = error
    const { message = '' } = response.data || {}
    let errorMessage = {
      message: 'Não foi possível acessar sua conta. Por favor, tente novamente.',
    }

    setErrorMessage(errorMessage)

    const errorActions = {
      [httpStatus.TOO_MANY_REQUESTS]: () => {
        let lurkerMessage = `rate_limit_exceeded`

        if (message === 'MAX_CHECK_ATTEMPTS_REACHED') {
          errorMessage = {
            title: 'Tentativas excedidas',
            message:
              'Limite de tentativas atingido. Enviamos um e-mail com novas instruções de acesso.',
          }
          setErrorMessage(errorMessage)
          lurkerMessage = 'max_account_check_attempts'
        }

        lurker.viewMessageEvt('error', {
          accounts_reason: lurkerMessage,
          accounts_status_code: response.status,
        })
      },
      [httpStatus.FORBIDDEN]: () => {
        if (message === 'INVALID_PIN_CODE' || message === 'INVALID_CODE') {
          errorMessage = {
            title: 'Código incorreto.',
            message: 'Não foi possível validar seu e-mail. Por favor, tente novamente.',
          }

          setErrorMessage(errorMessage)

          lurker.viewMessageEvt('error', {
            accounts_reason: 'invalid_pin_code',
            accounts_status_code: response.status,
          })
        }

        if (message === 'ACCOUNT_LOCKED') {
          errorMessage = {
            title: 'Conta bloqueada.',
            showContactUs: true,
          }

          setErrorMessage(errorMessage)

          lurker.viewMessageEvt('error', {
            accounts_reason: 'account_locked',
            accounts_status_code: response.status,
          })
        }
      },
      default: () => {
        setErrorMessage(errorMessage)
        lurker.viewMessageEvt('error', {
          accounts_reason: `unexpected_status_code`,
          accounts_status_code: error?.response.status,
        })
      },
    }

    const action =
      errorActions[response.status] || errorActions[error?.message] || errorActions.default
    return action()
  }

  const checkPinCode = code => {
    const changeEmail = !email?.value.includes('*')
    const emailValue = changeEmail ? email.value : ''

    lurker.clickEvt('submit')

    const checkPromise = changeEmail
      ? emailChangeCheckPinCode(code, emailValue, token, sendEmailRequestId)
      : confirmationCheckPinCode(code, token, 'EMAIL')

    checkPromise
      .then(data => {
        if (typeof onSuccess === 'function') {
          lurker.viewMessageEvt('success')

          onSuccess(data)
        }
      })
      .catch(error => {
        onApiError(error)
      })
      .finally(() => setIsSubmitting(false))
  }

  const onPinCodeChange = (pinCode, isValid) => {
    setIsSubmitting(false)

    if (isValid) {
      checkPinCode(pinCode)
      setIsSubmitting(true)
    }
  }

  return (
    <RevalidationCheckPinCodeTemplate
      title="Confirme o seu e-mail"
      description="Digite o código enviado para"
      alertDescription="O código é privado. Não compartilhe essa informação com ninguém."
      infoDescription="Deseja reenviar o código para esse e-mail"
      data={email?.value}
      lockTime={lockTime}
      onBack={onBackClick}
      resendCode={resendEmailPicode}
      isResending={isResending}
      isSubmitting={isSubmitting}
      error={errorMessage}
      closeResendCodeSuccessMessage={closeResendCodeSuccessMessage}
      onPinCodeChange={onPinCodeChange}
      showResendCodeSuccessMessage={showResendCodeSuccessMessage}
      remainingAttempts={defaultRemainingAttempts}
    />
  )
}

RevalidationEmailCheckPinCodePage.propTypes = {
  onBackClick: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
}
